import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { sub } from 'date-fns';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from '../../../services/theme.service';
import { TABLE_CONFIGURATION } from '../../models/constants';
import {
  EntityType,
  FilterOrSortDropdownType,
  FilterSelectedEventType,
  SelectOptionType,
  TableConfig,
  TableTypes
} from '../../models/types';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  public translateService = inject(TranslateService);

  canOrder = false;
  currentFilters: any = {};
  fields: FilterOrSortDropdownType[] = [];
  private _hasFilters = false;
  isInnerTable = false;
  isLight?: boolean;
  isStripedTable = false;
  isSimple = false;
  sortingOptions: FilterOrSortDropdownType[] = [];
  tableConfig!: TableConfig;
  tableHeadingsKeys: (keyof EntityType)[] = [];
  tableType!: TableTypes;
  translateOptions = true;
  values: any;
  isReady = false;
  isLoadingPaginate = false;
  filtersTmp$!: Observable<any>;
  private _refreshFilters$ = new BehaviorSubject<boolean>(false);

  private _keyword$ = new Subject<string>();

  keywordSearchValue = '';

  @Input() type = '';
  @Input() hasChart = false;
  @Input() allValues$!: Observable<any>;
  @Input() filters$!: Observable<any>;
  @Input() isMachinePerformanceDetails = false;
  @Input() isExternalLoading = false;
  @Input() prevPage?: number;
  @Input() currentPage? = 1;
  @Input() nextPage?: number;
  @Input() totalPages?: number;
  @Input() showTitle = true;
  @Input() hasKeywordFilter = false;
  @Input() useInlineKeywordFilter = false;
  @Input() keywordFilterName = 'keyword';
  @Input() keywordFilterPlaceholder = 'FILTER.SEARCH_PLACEHOLDER';
  @Input() set initialFilters(filters: any) {
    if (filters && Object.keys(filters).length > 0) {
      this.currentFilters = { ...filters };
      this.keywordSearchValue = filters[this.keywordFilterName] || '';
      if (this._refreshFilters$) {
        this._refreshFilters$.next(true);
      }
      this._cdr.markForCheck();
    }
  }
  @Output() selectedFilter = new EventEmitter<FilterSelectedEventType[]>();
  @Output() selectedPage = new EventEmitter<number>();
  @Output() deleteRow = new EventEmitter<any>();

  showDeleteConfirm = false;
  pendingDeleteRow: any = null;

  constructor() {
    /* noop */
  }

  onDeleteRequested(row: any): void {
    this.pendingDeleteRow = row;
    this.showDeleteConfirm = true;
    this._cdr.markForCheck();
  }

  confirmDelete(): void {
    if (this.pendingDeleteRow) {
      this.deleteRow.emit(this.pendingDeleteRow);
    }
    this.cancelDelete();
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.pendingDeleteRow = null;
    this._cdr.markForCheck();
  }

  ngOnInit() {
    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._keyword$
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((value: string) => {
        this.isReady = false;

        const nonKeywordFilters = Object.keys(this.currentFilters)
          .filter((key) => key !== this.keywordFilterName)
          .map((key) => ({
            name: key,
            selectedValue: this.currentFilters[key],
            operation: 'filter' as const,
            text: this.currentFilters[key]
          }));

        const event: FilterSelectedEventType[] = [...nonKeywordFilters];

        if (value && value.trim()) {
          event.push({
            name: this.keywordFilterName,
            selectedValue: value,
            operation: 'filter',
            text: value
          });

          this.currentFilters = {
            ...this.currentFilters,
            [this.keywordFilterName]: value
          };
        } else {
          const rest = { ...this.currentFilters };
          delete rest[this.keywordFilterName];
          this.currentFilters = rest;
        }

        this.selectedFilter.emit(event);
        this._cdr.markForCheck();
      });

    this.tableType = this._getTableType();
    this.tableConfig = TABLE_CONFIGURATION[this.tableType];

    this.tableHeadingsKeys = this.tableConfig.headings?.map((config) => config.key) as (keyof EntityType)[];

    this.isStripedTable = this.tableConfig.css.table.striped;
    this.isSimple = this.tableConfig.css.table.simple;

    this.allValues$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((allValues) => {
      this.isReady = true;
      this.isLoadingPaginate = false;
      this.values = allValues;
      this._cdr.markForCheck();
    });

    this.filtersTmp$ = this._refreshFilters$.pipe(
      takeUntilDestroyed(this._destroyRef),
      switchMap(() => {
        return this.filters$.pipe(
          tap((filters) => {
            this.setFilters(filters);
            this._cdr.markForCheck();
          })
        );
      })
    );
    this._cdr.markForCheck();
  }

  private _getTableType(): TableTypes {
    switch (this.type) {
      case 'machines_performance':
        return TableTypes.MACHINES_PERFORMANCE;

      case 'open_tickets':
        return TableTypes.OPEN_TICKETS;

      case 'closed_tickets':
        return TableTypes.CLOSED_TICKETS;

      case 'requests':
        return TableTypes.REQUESTS;

      case 'final_users':
        return TableTypes.FINAL_USERS;

      case 'last_final_user':
        return TableTypes.LAST_FINAL_USER;

      case 'machines':
        this.translateOptions = false;
        return TableTypes.MACHINES;

      case 'users':
        return TableTypes.USER;

      case 'partners':
        return TableTypes.PARTNER;

      case 'customer_partners':
        return TableTypes.USER;

      case 'sales-order':
        return TableTypes.SALES_ORDER;

      case 'required_document':
        return TableTypes.REQUIRED_DOCUMENT;

      case 'sales_order_documents':
        return TableTypes.SALES_ORDER_DOCUMENTS;

      case 'banners':
        return TableTypes.BANNER;

      case 'learning':
        return TableTypes.LEARNING;

      case 'company_group':
        return TableTypes.COMPANY_GROUP;

      case 'company_group_user':
        return TableTypes.COMPANY_GROUP_USER;

      case 'legal_documents':
        return TableTypes.LEGAL_DOCUMENTS;

      case 'machine_modules':
        return TableTypes.MACHINE_MODULES;

      default:
        return TableTypes.MACHINES;
    }
  }

  onSelectedFilter(event: FilterSelectedEventType[]) {
    const hasKeywordInEvent = event.some((filter) => filter.name === this.keywordFilterName);
    this.isReady = false;

    event.forEach((filter) => {
      if (filter.name === 'accountId' && filter.metadata && filter.metadata.accountId) {
        this.currentFilters = {
          ...this.currentFilters,
          [filter.name]: filter.metadata.accountId
        };
      }
      if (filter.name === 'operatingSector') {
        this.currentFilters = {
          ...this.currentFilters,
          [filter.name]: filter.selectedValue
        };
      } else {
        this.currentFilters = {
          ...this.currentFilters,
          [filter.name]: filter.selectedValue
        };
      }
    });

    if (!hasKeywordInEvent && this.keywordSearchValue && this.keywordSearchValue.trim()) {
      this.currentFilters = {
        ...this.currentFilters,
        [this.keywordFilterName]: this.keywordSearchValue
      };

      event.push({
        name: this.keywordFilterName,
        selectedValue: this.keywordSearchValue,
        operation: 'filter',
        text: this.keywordSearchValue
      });
    }

    this.selectedFilter.emit(event);
  }

  goToPage(page?: number) {
    if (page) {
      this.isLoadingPaginate = true;
      this.selectedPage.emit(page);
    }
  }

  private setFilters(values?: any) {
    const availableFilters = values;
    if (this.tableType !== TableTypes.MACHINES_PERFORMANCE) {
      if (this._hasFilters) {
        this.fields = [];
        this.sortingOptions = [];
        this._hasFilters = false;
      }
      if (!availableFilters || Object.keys(availableFilters).length < 0) return;
      this._hasFilters = true;

      for (const filter in availableFilters) {
        if (!this.tableConfig.available_filters?.includes(filter)) continue;

        const elems = availableFilters[filter];

        if (filter === 'accounts' && elems && typeof elems === 'object' && !Array.isArray(elems)) {
          const accountOptions = Object.entries(elems).map(([accountId, accountName]) => ({
            id: accountId,
            value: accountName as string,
            selected: this.currentFilters ? this.currentFilters['accountId'] === accountId : false
          }));

          if (accountOptions.length > 0) {
            this.fields.push({
              key: 'accountId',
              values: accountOptions as SelectOptionType[]
            });
          }
          continue;
        }

        if (elems && Array.isArray(elems)) {
          if (filter === 'orderBy') {
            this.sortingOptions.push({
              key: 'orderBy',
              values: elems.map((f: string) => ({
                id: f,
                value: f,
                selected: this.currentFilters ? this.currentFilters[filter] === f : false
              })) as SelectOptionType[]
            });
            this.canOrder = true;
            continue;
          }

          this.fields.push({
            key: filter,
            values: elems.map((f: any) => {
              if (filter === 'customerCanUpload' || filter === 'associatedToMachine' || filter === 'isFavorite') {
                const stringValue = f.toString();
                return {
                  id: stringValue,
                  value: stringValue,
                  selected: this.currentFilters ? this.currentFilters[filter]?.toString() === stringValue : false
                };
              }

              return {
                id: f,
                value: f,
                selected: this.currentFilters ? this.currentFilters[filter] === f : false
              };
            }) as SelectOptionType[]
          });
        }
      }
    } else {
      if (this._hasFilters) return;
      this._hasFilters = true;
      this.translateOptions = true;
      const days = [7, 30, 365];
      const today = new Date();
      this.fields.push({
        key: 'date_period',
        values: days.map((day) => ({
          id: `${today.toISOString()} - ${sub(today, {
            days: day
          }).toISOString()}`,
          value: `FILTER.last_${day}_days`
        }))
      });
      this.fields.push({ key: 'custom_period', values: [] });
    }
  }

  resetFilters() {
    this.currentFilters = {};
    this.keywordSearchValue = '';
    this.canOrder = false;
    this._hasFilters = false;
    this.fields = [];
    this.sortingOptions = [];
    this._refreshFilters$.next(true);
  }

  onKeywordSearch(value: string): void {
    if (!value || !value.trim()) {
      this.keywordSearchValue = '';
      this.isReady = false;

      const nonKeywordFilters = Object.keys(this.currentFilters)
        .filter((key) => key !== this.keywordFilterName)
        .map((key) => ({
          name: key,
          selectedValue: this.currentFilters[key],
          operation: 'filter' as const,
          text: this.currentFilters[key]
        }));

      const event: FilterSelectedEventType[] = [...nonKeywordFilters];

      const rest = { ...this.currentFilters };
      delete rest[this.keywordFilterName];
      this.currentFilters = rest;

      this.selectedFilter.emit(event);
      return;
    }

    this.keywordSearchValue = value;
    this._keyword$.next(value);
  }
}
